import React, { useState, useEffect } from "react";
import { Box, Typography, styled, Button, useTheme } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import HaefniImage from "assets/logos/haefni2.jpg";
import KristjanImage from "assets/logos/kristjan_image.png";
import GlodisImage from "assets/logos/glodis_image.png";
import CorporateImage from "assets/logos/corporate_learning_coach_image.png";

import GermanyFlag from "assets/icons/de_flag.png";
import UKFlag from "assets/icons/us_uk_flag.png";
import SpainFlag from "assets/icons/es_flag.png";
import TurkeyFlag from "assets/icons/tr_flag.png";
import PolandFlag from "assets/icons/pl_flag.png";
import ItalyFlag from "assets/icons/it_flag.png";
import FranceFlag from "assets/icons/fr_flag.png";

import RedHeart from "assets/icons/filled_heart.png";
import GreyHeart from "assets/icons/red_unfilled_heart.png";

const assistants = [
  {
    id: 1,
    name: "Hæfni",
    img: HaefniImage,
    description: {
      en: "Hæfni gives you examples and explanations, tells vivid little stories from the business world, humorous.",
      de: "Gibt dir Beispiele und Erläuterungen, erzählt anschaulich kleine Stories aus der Businesswelt, humorvoll.",
    },
    form: {
      en: "She uses a casual speech style",
      de: "Verwendet die Du-Form",
    },
    available: true,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 2,
    name: "Kristján",
    img: KristjanImage,
    description: {
      en: "He uses a rather formal speech style",
      de: "Vermittelt Ihnen die wichtigsten Tipps & Tricks kurz und knapp, schnörkellos auf den Punkt.",
    },
    form: {
      en: "He uses a rather formal speech style. ",
      de: "Verwendet die Sie-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 3,
    name: "Glódís",
    img: GlodisImage,
    description: {
      en: "Glódís explains the best tips briefly and clearly. Some details and topics are omitted.",
      de: "Formuliert verständlich in einfacher Sprache. Einige Details und Themen werden weggelassen.",
    },
    form: {
      en: "She uses a simple language. ",
      de: "Verwendet die Du-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
  {
    id: 4,
    name: "Corporate",
    img: CorporateImage,
    description: {
      en: "The avatar and look & feel of the chatbot can be customized according to your corporate design.",
      de: "Avatar und Look & Feel des Chatbots kann nach Ihrem Corporate Design ausgestattet werden.",
    },
    form: {
      en: "",
      de: "Du oder Sie-Form",
    },
    available: false,
    activeLanguages: [GermanyFlag],
  },
];

const languages = [
  { flag: GermanyFlag, active: true },
  { flag: UKFlag, active: false },
  { flag: SpainFlag, active: false },
  { flag: ItalyFlag, active: false },
  { flag: PolandFlag, active: false },
  { flag: TurkeyFlag, active: false },
  { flag: FranceFlag, active: false },
];

const Container = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "flex-start",
  width: "100%",
  maxWidth: "960px",
  margin: "0 auto",
  padding: "4rem 1rem 2rem 1rem",
});

const CardContainer = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "1.2rem",
  justifyItems: "center",
  width: "100%",

  [theme.breakpoints.down("md")]: {
    gridTemplateColumns: "repeat(2, 1fr)",
  },
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
  },
}));


const AssistantCard = styled(Box)<{ selected?: boolean }>(({ selected }) => ({
  width: "220px",
  minHeight: "auto",
  height: "100%",
  padding: "1.2rem",
  borderRadius: "16px",
  textAlign: "center",
  boxShadow: "0 4px 10px rgba(0, 0, 0, 0.1)",
  border: selected ? "4px solid #a1b7a5" : "2px solid #e0e0e0",
  backgroundColor: "#fff",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
  alignItems: "center",
  position: "relative",
  transition: "all 0.3s ease",
  cursor: "pointer",

  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 6px 18px rgba(0, 0, 0, 0.1)",
  },
}));

const AvatarWrapper = styled(Box)({
  width: "90px",
  height: "90px",
  marginTop: "1.5rem",
});


const AvatarImage = styled("img")({
  width: "100%",
  height: "100%",
  objectFit: "contain",
});

const NameText = styled(Typography)({
  fontSize: "1.25rem",
  fontWeight: "bold",
  textAlign: "center",
  letterSpacing: "0.05em",
});

const DescriptionText = styled(Typography)({
  fontSize: "1rem",
  textAlign: "center",
  color: "#333",
  padding: "0 1rem",
  maxWidth: "95%",
  overflow: "visible",
  textOverflow: "unset",
  whiteSpace: "normal",
  "@media (max-width: 1024px)": {
    fontSize: "0.9rem",
  },
  "@media (max-width: 768px)": {
    fontSize: "0.85rem",
    padding: "0 0.5rem",
  },
});

const FormText = styled(Typography)({
  fontSize: "1rem",
  fontWeight: "bold",
  color: "#4A4A4A",
  marginTop: "0.5rem",
  marginBottom: "0.3rem",
});

const LanguageContainer = styled(Box)({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  gap: "0.5rem",
  marginTop: "1rem",

  "@media (max-width: 768px)": {
    flexDirection: "column",
    gap: "0.7rem",
    marginTop: "1.5rem",
  },
});

const LanguageIcons = styled(Box)({
  display: "flex",
  justifyContent: "center",
  gap: "8px",
  alignItems: "center",
  paddingLeft: "12px",
  paddingRight: "12px",
});

const LanguageCircle = styled(Box)(({ active }: { active?: boolean }) => ({
  width: "22px",
  height: "22px",
  aspectRatio: "1 / 1",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  borderRadius: "50%",
  backgroundColor: active ? "transparent" : "#d3d3d3",
  filter: active ? "none" : "grayscale(100%)",
  overflow: "hidden",
  boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.2)",
  transition: "transform 0.2s ease",
  "&:hover": {
    boxShadow: "0 6px 16px rgba(0,0,0,0.1)",
    transform: "translateY(-4px)",
  }
}));

const ComingSoonText = styled(Typography)({
  fontSize: "0.95rem",
  fontWeight: "bold",
  color: "red",
  marginTop: "0.5rem",
});

const HeartContainer = styled(Box)({
  position: "absolute",
  top: "-12%",
  left: "50%",
  transform: "translateX(-50%)",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  cursor: "pointer",
  transition: "transform 0.2s ease",

  "@media (max-width: 768px)": {
    top: "-5%",
  },
});

const SkillaryButton = styled(Button)(({ disabled }: { disabled: boolean }) => ({
  marginTop: "2rem",
  padding: "0.3rem 5rem",
  borderRadius: "0px",
  backgroundColor: disabled ? "#d3d3d3" : "#a1b7a5",
  color: "white",
  fontSize: "1.2rem",
  fontWeight: "bold",
  textTransform: "none",
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  margin: "2rem auto",
  cursor: disabled ? "default" : "pointer",
  "@media (max-width: 1024px)": {
    padding: "0.3rem 4rem",
    fontSize: "1rem",
    marginBottom: "4rem",
  },
  "@media (max-width: 768px)": {
    padding: "0.5rem 3rem",
    fontSize: "0.9rem",
  },
}));


const AssistantSelection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { i18n, t } = useTranslation();

  const [avatar, setAvatar] = useState<string | null>(null);
  const [selectedAssistant, setSelectedAssistant] = useState<number | null>(null);
  const [selectedLanguages, setSelectedLanguages] = useState<{ [key: number]: string }>({});

  useEffect(() => {
    const storedAssistant = localStorage.getItem("selectedAssistant");
    const storedAvatar = localStorage.getItem("selectedAvatar");

    if (storedAssistant) {
      const assistantId = parseInt(storedAssistant, 10);
      const selected = assistants.find(a => a.id === assistantId);
      if (selected) {
        setSelectedAssistant(selected.id);
        setAvatar(selected.img);
      } else {
        setSelectedAssistant(assistants[0].id);
        setAvatar(assistants[0].img);
        localStorage.setItem("selectedAssistant", String(assistants[0].id));
        localStorage.setItem("selectedAvatar", assistants[0].img);
      }
    } else {
      setSelectedAssistant(assistants[0].id);
      setAvatar(assistants[0].img);
      localStorage.setItem("selectedAssistant", String(assistants[0].id));
      localStorage.setItem("selectedAvatar", assistants[0].img);
    }
  }, []);


  const handleSelect = (id: number) => {
    const selected = assistants.find(a => a.id === id);
    if (!selected || !selected.available) return;
    if (selectedAssistant === id) return;

    setSelectedAssistant(id);
    setAvatar(selected.img);
    localStorage.setItem("selectedAssistant", String(id));
    localStorage.setItem("selectedAvatar", selected.img);
  };

  return (
    <Container>
      <CardContainer>
        {assistants.map((assistant) => (
          <Box key={assistant.id} sx={{ position: "relative", width: "220px" }}>
            {!assistant.available && (
              <Typography
                sx={{
                  position: "absolute",
                  top: "1.5rem",
                  left: "50%",
                  transform: "translateX(-50%)",
                  color: "#e53935",
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  zIndex: 10,
                  

                }}
              >
                {t("Coming Soon")}
              </Typography>
            )}
  
            <AssistantCard
              selected={selectedAssistant === assistant.id}
              onClick={() => handleSelect(assistant.id)}
              sx={{
                height: "100%",
                filter: assistant.available ? "none" : "grayscale(100%)",
                transition: "0.3s ease",
                minHeight: "480px",
                cursor: assistant.available ? "pointer" : "not-allowed",
              }}
            >
              <HeartContainer>
                <img
                  src={selectedAssistant === assistant.id ? RedHeart : GreyHeart}
                  alt="heart-icon"
                  width="40px"
                  height="40px"
                  style={{ cursor: "pointer" }}
                />
              </HeartContainer>
  
              <AvatarWrapper>
                <AvatarImage src={assistant.img} alt={assistant.name} />
              </AvatarWrapper>
  
              <NameText>
                {assistant.name === "Hæfni" ? (
                  <>
                    <span style={{ color: "#002e5a" }}>Hæf</span>
                    <span style={{ color: "#D72638" }}>ni</span>
                  </>
                ) : assistant.name === "Kristján" ? (
                  <span style={{ color: "#002e5a" }}>{assistant.name}</span>
                ) : assistant.name === "Glódís" ? (
                  <>
                    <span style={{ color: "#002e5a" }}>Gló</span>
                    <span style={{ color: "#D72638" }}>dís</span>
                  </>
                ) : (
                  <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center">
                    <span style={{ color: "#D72638" }}>Corporate</span>
                    <span style={{ color: "#002e5a" }}>Lerncoach</span>
                  </Box>
                )}
              </NameText>
  
              <DescriptionText>
                {assistant.description[i18n.language as "en" | "de"]}
              </DescriptionText>
  
              <FormText>{assistant.form[i18n.language as "en" | "de"]}</FormText>
  
              <LanguageContainer>
                <LanguageIcons>
                  {languages.map((lang, index) => {
                    const isActive = assistant.activeLanguages.includes(lang.flag);
                    return (
                      <LanguageCircle key={index} active={isActive}>
                        <img
                          src={lang.flag}
                          alt="flag"
                          width="100%"
                          height="100%"
                          style={{
                            objectFit: "cover",
                            borderRadius: "50%",
                            filter: isActive ? "none" : "grayscale(100%)",
                            opacity: isActive ? "1" : "0.5",
                          }}
                        />
                      </LanguageCircle>
                    );
                  })}
                </LanguageIcons>
              </LanguageContainer>
            </AssistantCard>
          </Box>
        ))}
      </CardContainer>
    </Container>
  );
  
  



};

export default AssistantSelection;
